import React, { useCallback, useRef, useEffect, useState } from 'react';
import { AlertCircle, ChevronLeft, ChevronRight, Home } from 'lucide-react';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { OnboardingState } from '@/types/onboarding';
import { StepWrapper } from './StepWrapper';
import { useOnboardingSteps } from '@/hooks/use-onboarding-steps';
import { cn } from '@/lib/utils';
import '@/styles/mobile-onboarding.css';
import { Link } from 'react-router-dom';

// Reutilizando a mesma interface do OnboardingWizard para garantir compatibilidade
interface MobileOnboardingWizardProps {
  userId: string;
  currentState: OnboardingState;
  onStepComplete: (stepId: string, data: Record<string, any>) => void;
  onNavigateToStep?: (stepNumber: number) => void;
  allowNavigation?: boolean;
}

export const MobileOnboardingWizard: React.FC<MobileOnboardingWizardProps> = ({ 
  userId, 
  currentState,
  onStepComplete,
  onNavigateToStep,
  allowNavigation = true
}) => {
  // Ref para rastrear se o componente está montado
  const isMounted = useRef(true);
  
  // Definir isMounted como false no cleanup
  useEffect(() => {
    // Inicializar o ref
    isMounted.current = true;
    
    // Cleanup function para quando o componente desmontar
    return () => {
      isMounted.current = false;
    };
  }, []);

  // Usar o hook personalizado para obter os passos e funções auxiliares
  const { 
    steps, 
    getStepByNumber, 
    isLastStep: isStepLast,
    getProgressPercentage
  } = useOnboardingSteps();

  // Certificar que a etapa atual é válida
  const currentStep = Math.min(
    Math.max(0, typeof currentState?.current_step === 'number' ? currentState.current_step : 0), 
    steps.length - 1
  );
  
  // Obter o componente e informações para a etapa atual
  const currentStepObj = getStepByNumber(currentStep);
  const CurrentStepComponent = currentStepObj?.component;
  const isFirstStep = currentStep === 0;
  const isLastStep = isStepLast(currentStepObj?.id || '');
  
  // Manipulador seguro para completar uma etapa
  const handleStepComplete = useCallback((data: Record<string, any>) => {
    // Verificações defensivas
    if (!isMounted.current || !currentStepObj) {
      return;
    }
    
    try {
      // Obter o ID do passo atual
      const stepId = currentStepObj.id;
      
      // Chamar o manipulador do componente pai se estiver montado
      if (isMounted.current && typeof onStepComplete === 'function') {
        onStepComplete(stepId, data);
      }
    } catch (error) {
      console.error('Error in handleStepComplete:', error);
    }
  }, [currentStepObj, onStepComplete]);

  // Log das props recebidas
  useEffect(() => {
    console.log("MobileOnboardingWizard: Props recebidas:", {
      currentStep,
      hasNavigateFunction: !!onNavigateToStep,
      allowNavigation
    });
  }, [currentStep, onNavigateToStep, allowNavigation]);

  // Função para navegar para o próximo passo
  const handleNextStep = useCallback(() => {
    console.log("MobileOnboardingWizard: Tentando navegar para o próximo passo:", currentStep + 1);
    
    // Confirmação de que temos a função de navegação
    if (typeof onNavigateToStep !== 'function') {
      console.error("MobileOnboardingWizard: onNavigateToStep não é uma função!", onNavigateToStep);
      return;
    }
    
    if (isLastStep) {
      console.log("MobileOnboardingWizard: Este é o último passo, não navegando");
      return;
    }
    
    if (!allowNavigation) {
      console.log("MobileOnboardingWizard: Navegação não permitida");
      return;
    }
    
    // Chamada da função com o número correto do próximo passo
    console.log("MobileOnboardingWizard: Navegando para o passo", currentStep + 1);
    onNavigateToStep(currentStep + 1);
  }, [currentStep, onNavigateToStep, isLastStep, allowNavigation]);

  // Função para navegar para o passo anterior
  const handlePreviousStep = useCallback(() => {
    console.log("MobileOnboardingWizard: Tentando navegar para o passo anterior:", currentStep - 1);
    
    // Confirmação de que temos a função de navegação
    if (typeof onNavigateToStep !== 'function') {
      console.error("MobileOnboardingWizard: onNavigateToStep não é uma função!", onNavigateToStep);
      return;
    }
    
    if (isFirstStep) {
      console.log("MobileOnboardingWizard: Este é o primeiro passo, não navegando");
      return;
    }
    
    if (!allowNavigation) {
      console.log("MobileOnboardingWizard: Navegação não permitida");
      return;
    }
    
    // Chamada da função com o número correto do passo anterior
    console.log("MobileOnboardingWizard: Navegando para o passo", currentStep - 1);
    onNavigateToStep(currentStep - 1);
  }, [currentStep, onNavigateToStep, isFirstStep, allowNavigation]);

  // Verificar se o componente é válido e se existe
  const isValidStep = typeof CurrentStepComponent === 'function';
  
  // Se o componente não existe, mostrar um erro
  if (!isValidStep || !currentStepObj) {
    console.error(`Componente de passo inválido na posição ${currentStep}`);
    return (
      <div className="container mx-auto py-4 px-4">
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Erro ao carregar etapa</AlertTitle>
          <AlertDescription>
            Não foi possível carregar o componente da etapa atual. 
            Por favor, tente atualizar a página ou contate o suporte.
          </AlertDescription>
        </Alert>
      </div>
    );
  }
  
  // Obter os dados salvos para o passo atual
  const currentStepData = currentState?.steps_data?.[currentStepObj.id] || {};

  // Indicador de progresso para mobile
  const progressPercentage = getProgressPercentage(currentStep);

  return (
    <div className="mobile-onboarding-container flex flex-col min-h-[100vh]">
      {/* Cabeçalho fixo com indicador de progresso */}
      <div className="mobile-onboarding-header">
        <div className="px-4 py-2">
          <div className="flex justify-between items-center mb-1">
            <h1 className="text-base font-semibold">{currentStepObj.title}</h1>
            <span className="text-xs font-medium text-gray-500">
              {currentStep + 1}/{steps.length}
            </span>
          </div>
          <p className="text-xs text-muted-foreground line-clamp-2">{currentStepObj.description}</p>
          
          {/* Barra de progresso */}
          <div className="h-1.5 w-full bg-gray-100 rounded-full mt-2 overflow-hidden">
            <div 
              className="h-full bg-primary rounded-full transition-all duration-500"
              style={{ width: `${progressPercentage}%` }}
            />
          </div>
        </div>
      </div>

      {/* Conteúdo principal - com classe para espaçamento adequado */}
      <div className="mobile-onboarding-content flex-1 px-3 py-3">
        <StepWrapper onNext={handleStepComplete}>
          <CurrentStepComponent 
            data={currentStepData}
            isLastStep={isLastStep}
            onNext={() => {}} /* Será substituído pelo StepWrapper */
          />
        </StepWrapper>
      </div>
      
      {/* Botões de navegação fixos no rodapé */}
      <div className="mobile-onboarding-footer">
        {/* Conjunto de três botões: Anterior, Home, Próximo */}
        <div className="flex w-full justify-between">
          {/* Botão Anterior */}
          <Button 
            variant="outline"
            onClick={handlePreviousStep}
            disabled={isFirstStep || !allowNavigation || typeof onNavigateToStep !== 'function'}
            className="w-[30%] gap-1 text-xs px-1"
            type="button"
          >
            <ChevronLeft className="h-4 w-4 flex-shrink-0" />
            <span className="truncate">Anterior</span>
          </Button>
          
          {/* Botão Home */}
          <Link to="/dashboard" className="w-[30%]">
            <Button 
              variant="outline"
              className="w-full gap-1 text-xs home-button px-1"
              type="button"
            >
              <Home className="h-4 w-4 flex-shrink-0" />
              <span className="truncate">Início</span>
            </Button>
          </Link>
          
          {/* Botão Próximo ou Concluir */}
          {isLastStep ? (
            <Button 
              variant="default"
              onClick={() => {
                console.log("MobileOnboardingWizard: Botão Concluir clicado");
                handleStepComplete({ completed: true });
              }}
              className="w-[30%] gap-1 text-xs px-1"
              type="button"
            >
              <span className="truncate">Concluir</span>
              <ChevronRight className="h-4 w-4 flex-shrink-0" />
            </Button>
          ) : (
            <Button 
              variant="default"
              onClick={handleNextStep}
              disabled={isLastStep || !allowNavigation || typeof onNavigateToStep !== 'function'}
              className="w-[30%] gap-1 text-xs px-1"
              type="button"
            >
              <span className="truncate">Próximo</span>
              <ChevronRight className="h-4 w-4 flex-shrink-0" />
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}; 